import { useState } from 'react';
import type { Platform } from '../api.js';

/**
 * Painel de programação recorrente.
 *
 * Existe porque "postar um achadinho todo dia" é o trabalho real, e agendar 90
 * posts à mão não é trabalho, é castigo. A programação GERA posts na fila — não
 * publica direto — então cada ocorrência continua visível e cancelável sozinha
 * na Agenda.
 */

const DIAS = [
  { n: 1, label: 'seg' },
  { n: 2, label: 'ter' },
  { n: 3, label: 'qua' },
  { n: 4, label: 'qui' },
  { n: 5, label: 'sex' },
  { n: 6, label: 'sáb' },
  { n: 0, label: 'dom' },
];

/**
 * Presets de duração.
 *
 * "Sem prazo" é o caso que o usuário pediu por nome, e fica junto dos prazos
 * fechados para deixar claro que é a mesma decisão — não um modo escondido.
 */
const DURACOES = [
  { dias: 7, label: '7 dias' },
  { dias: 30, label: '30 dias' },
  { dias: 90, label: '90 dias' },
  { dias: null, label: 'Sem prazo' },
] as const;

export type RecurrenceForm = {
  times: string[];
  weekdays: number[];
  /** null = por tempo indeterminado */
  dias: number | null;
};

type Props = {
  platform: Platform;
  /** blockers do rascunho; a programação exige o mesmo que um post avulso */
  blockers: string[];
  saving: boolean;
  onCreate: (form: RecurrenceForm) => Promise<void>;
};

export function RecurrencePanel({ platform, blockers, saving, onCreate }: Props) {
  const [aberto, setAberto] = useState(false);
  const [times, setTimes] = useState<string[]>(['09:00', '19:30']);
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [dias, setDias] = useState<number | null>(30);

  const horariosValidos = times.filter((t) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t));
  const impedimentos = [
    ...blockers,
    horariosValidos.length === 0 ? 'Informe ao menos um horário' : null,
  ].filter((x): x is string => Boolean(x));

  // Quantos posts isso vai gerar: número concreto vale mais que "vai repetir".
  const porDia = horariosValidos.length;
  const diasAtivos = weekdays.length === 0 ? 7 : weekdays.length;
  const totalPrevisto = dias === null ? null : Math.round((porDia * diasAtivos * dias) / 7);

  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-card)',
        padding: aberto ? '14px 16px 16px' : '10px 12px',
        marginBottom: 18,
        background: 'var(--surface-2)',
      }}
    >
      <button
        type="button"
        className="ghost"
        aria-expanded={aberto}
        onClick={() => setAberto((v) => !v)}
        style={{ width: '100%', justifyContent: 'space-between', display: 'flex' }}
      >
        <span style={{ fontWeight: 600, color: 'var(--ink)' }}>
          {aberto ? '▾' : '▸'} Repetir automaticamente
        </span>
        <span className="muted" style={{ fontSize: 12 }}>
          {aberto ? '' : 'em vez de agendar um por um'}
        </span>
      </button>

      {aberto && (
        <div className="form-grid" style={{ gap: 14, marginTop: 14 }}>
          <div>
            <label>Horários do dia</label>
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              {times.map((t, i) => (
                <div
                  key={i}
                  style={{ display: 'flex', gap: 4, alignItems: 'center', flex: '0 0 auto' }}
                >
                  <input
                    type="time"
                    value={t}
                    onChange={(e) =>
                      setTimes((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))
                    }
                    style={{ width: 116 }}
                  />
                  {times.length > 1 && (
                    <button
                      type="button"
                      className="ghost"
                      aria-label={`Remover o horário ${t}`}
                      title="Remover"
                      onClick={() => setTimes((prev) => prev.filter((_, j) => j !== i))}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              {times.length < 12 && (
                <button
                  type="button"
                  className="ghost"
                  style={{ flex: '0 0 auto' }}
                  onClick={() => setTimes((prev) => [...prev, '12:00'])}
                >
                  + horário
                </button>
              )}
            </div>
          </div>

          <div>
            <label>Dias da semana</label>
            <div className="row" style={{ gap: 6 }}>
              {DIAS.map((d) => {
                const on = weekdays.includes(d.n);
                return (
                  <button
                    key={d.n}
                    type="button"
                    className={on ? 'primary' : ''}
                    aria-pressed={on}
                    style={{ flex: '0 0 auto', padding: '5px 10px', fontSize: 12.5 }}
                    onClick={() =>
                      setWeekdays((prev) =>
                        on ? prev.filter((x) => x !== d.n) : [...prev, d.n],
                      )
                    }
                  >
                    {d.label}
                  </button>
                );
              })}
              {weekdays.length > 0 && (
                <button
                  type="button"
                  className="ghost"
                  style={{ flex: '0 0 auto' }}
                  onClick={() => setWeekdays([])}
                >
                  todos
                </button>
              )}
            </div>
            {weekdays.length === 0 && (
              <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
                Nenhum dia marcado = todos os dias.
              </p>
            )}
          </div>

          <div>
            <label>Por quanto tempo</label>
            <div className="row" style={{ gap: 6 }}>
              {DURACOES.map((d) => (
                <button
                  key={d.label}
                  type="button"
                  className={dias === d.dias ? 'primary' : ''}
                  aria-pressed={dias === d.dias}
                  style={{ flex: '0 0 auto', padding: '5px 12px', fontSize: 12.5 }}
                  onClick={() => setDias(d.dias)}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          {/* O número concreto: "vai repetir" não deixa ninguém decidir nada. */}
          <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>
            {porDia === 0
              ? 'Informe ao menos um horário.'
              : dias === null
                ? `${porDia} post(s) por dia em ${platform.toLowerCase()}, ` +
                  'sem data de fim — até você pausar.'
                : `Cerca de ${totalPrevisto} post(s) em ${platform.toLowerCase()} ao longo de ` +
                  `${dias} dias (${porDia} por dia).`}
          </p>

          {impedimentos.length > 0 && (
            <ul className="muted" style={{ margin: 0, paddingLeft: 18, fontSize: 12.5 }}>
              {impedimentos.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          )}

          <button
            type="button"
            className="primary"
            style={{ maxWidth: 220 }}
            disabled={saving || impedimentos.length > 0}
            onClick={() => void onCreate({ times: horariosValidos, weekdays, dias })}
          >
            {saving ? <span className="spinner" /> : 'Criar programação'}
          </button>
        </div>
      )}
    </div>
  );
}
